import {
  type NotesViewSearch,
  notesViewSearchShape,
} from '@/components/notes-view/notes-view-search';
import { z } from 'zod';

/**
 * The Cadrage tab's URL contract and its section registry (issue #3).
 *
 * Kept out of `routes/matrices.$matrixId.tsx` on purpose. The route module
 * needs a live router context to render, so `vitest.config.ts` excludes
 * `src/routes/**` from coverage and the project treats mounting a route in
 * isolation as impractical — the same reason `keyboard-shortcuts.ts` and
 * `wizard-step.ts` exist. Everything here is a pure function of its input, so
 * the URL rules (including the legacy `?tab=contexte` redirect) are unit-tested
 * rather than left to the e2e suite alone.
 */

/**
 * `?vue=cadrage` — a search parameter on the existing matrix route, not a child
 * route.
 *
 * That distinction is the load-bearing one. A child route would unmount
 * `MatrixGrid`, and with it the zoom, the selection and the scroll position a
 * consultant has just spent a minute arranging; the whole point of opening the
 * framing is to check something *against* the grid and come back. As a search
 * param the route component stays mounted and merely hides the grid behind
 * `display: none`, exactly as `matrix-wizard.tsx` keeps its four steps mounted.
 */
export interface MatrixViewSearch extends NotesViewSearch {
  vue?: 'cadrage' | 'notes' | undefined;
  tab?: 'historique' | 'regen' | 'contexte' | undefined;
}

/**
 * `?vue=notes` (issue #19) is the Notes view, at the same rank as Cadrage: the
 * grid stays mounted and hidden, the drawer is unmounted. Its filters, sort
 * and presentation ride on the same URL (see `notes-view-search.ts`) and are
 * only kept while the view is the Notes one.
 */
export const matrixViewSearchSchema = z
  .object({
    vue: z.enum(['cadrage', 'notes']).optional(),
    /**
     * The drawer's old deep link. `contexte` is no longer a drawer tab at all
     * (its editor moved into Cadrage), so it is accepted here only long enough
     * to be redirected — see `cadrageRedirect`. The two surviving tabs are
     * carried so an old link to either lands on the grid instead of 404-ing on
     * an unknown search param.
     */
    tab: z.enum(['historique', 'regen', 'contexte']).optional(),
    ...notesViewSearchShape,
  })
  .transform((parsed): MatrixViewSearch => {
    const search: MatrixViewSearch = {};
    if (parsed.vue !== undefined) search.vue = parsed.vue;
    if (parsed.tab !== undefined) search.tab = parsed.tab;
    if (parsed.vue !== 'notes') return search;
    if (parsed.statut !== undefined) search.statut = parsed.statut;
    if (parsed.niveau.length > 0) search.niveau = parsed.niveau;
    if (parsed.auteur.length > 0) search.auteur = parsed.auteur;
    if (parsed.cible.length > 0) search.cible = parsed.cible;
    if (parsed.q !== undefined && parsed.q !== '') search.q = parsed.q;
    if (parsed.tri !== undefined) search.tri = parsed.tri;
    if (parsed.ordre !== undefined) search.ordre = parsed.ordre;
    if (parsed.affichage !== undefined) search.affichage = parsed.affichage;
    return search;
  });

/**
 * The search object `?tab=contexte` should become, or `null` to leave the URL
 * alone.
 *
 * A redirect rather than a silent re-render: the Contexte tab's content now
 * lives at a different address, and a bookmark that keeps working while
 * displaying something else is the kind of thing nobody notices until they
 * share the link. `tab` is dropped from the result — carrying a drawer tab that
 * no longer exists forward would just re-trigger this on the next navigation.
 */
export function cadrageRedirect(search: MatrixViewSearch): { vue: 'cadrage' } | null {
  return search.tab === 'contexte' ? { vue: 'cadrage' } : null;
}

/** One entry of the sticky rail, and one `<section>` in the scroller beside it. */
export interface CadrageSection {
  /** Also the DOM id, so `#paliers` is a deep link into the section. */
  id: string;
  label: string;
  /**
   * True for the two sections that belong to the company rather than to this
   * matrix. They are shown because they feed the prompt and reading them here
   * beats opening another page; they are not editable here because they are
   * shared by every matrix of that company, so a change made from inside one
   * matrix would silently reach all the others. Each carries a link to the
   * company sheet instead. `ContexteTab` already excluded the company contexte
   * for this reason — this generalises the rule rather than inventing one.
   */
  readOnly?: boolean;
  /** Rendered only for `pms_admin`; the backend enforces the same gate (403). */
  adminOnly?: boolean;
}

/**
 * The six sections, in scroll order.
 *
 * Stacked sections rather than sub-tabs, deliberately: the point of gathering
 * the framing in one place is that it can be read as one thing. Sub-tabs would
 * rebuild the very fragmentation this replaces — the setup on one page, the
 * sujet in a 384px drawer, the prompt nowhere — only with shorter distances
 * between the pieces.
 */
export const CADRAGE_SECTIONS: readonly CadrageSection[] = [
  { id: 'setup', label: 'Setup de singularité', readOnly: true },
  { id: 'contexte-entreprise', label: "Contexte de l'entreprise", readOnly: true },
  { id: 'paliers', label: 'Paliers' },
  { id: 'sujet-contexte', label: 'Sujet & contexte' },
  { id: 'documents', label: 'Documents & digests' },
  { id: 'prompt', label: 'Prompt', adminOnly: true },
];

/** The sections a given role actually sees — the rail and the scroller must agree. */
export function visibleCadrageSections(isAdmin: boolean): CadrageSection[] {
  return CADRAGE_SECTIONS.filter((section) => !section.adminOnly || isAdmin);
}

/**
 * The section a `#fragment` names, or `null` for anything else (including the
 * `#prompt` of a user who cannot see it — landing on a section that is not
 * rendered would scroll nowhere and look broken).
 */
export function sectionIdFromHash(hash: string, isAdmin: boolean): string | null {
  const id = hash.replace(/^#/, '');
  return visibleCadrageSections(isAdmin).some((section) => section.id === id) ? id : null;
}
